import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
import {
	AbacusService,
	CalculateAudienceRequestSchema,
	CreateAudienceRequestSchema,
	DeleteAudienceRequestSchema,
	DuplicateAudienceRequestSchema,
	ListAudiencesRequestSchema,
	UpdateAudienceRequestSchema,
	type Audience,
	type AudienceConfig,
	type AudienceType,
	type CalculateAudienceRequest,
	type CalculateAudienceResponse,
	type CreateAudienceRequest,
	type CreateAudienceResponse,
	type DeleteAudienceRequest,
	type DeleteAudienceResponse,
	type DuplicateAudienceRequest,
	type DuplicateAudienceResponse,
	type ListAudiencesRequest,
	type ListAudiencesResponse,
	type UpdateAudienceRequest,
	type UpdateAudienceResponse,
} from "#gen/abacus/v2/abacus_pb.js";
import { type PaginationArguments } from "#gen/common/v1/pagination_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export class AudiencesClient {
	private client: Client<typeof AbacusService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.abacus || "/", AbacusService, options);
	}

	async getAudience(id: string): Promise<Audience> {
		try {
			const res = await this.client.getAudience({ id });
			return res.audience!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listAudiences(orgId: string, workspaceId?: string, name?: string, userId?: string, creatorEmail?: string, pagination?: PaginationArguments, options?: CallOptions): Promise<ListAudiencesResponse> {
		try {
			const params: ListAudiencesRequest = create(ListAudiencesRequestSchema, {
				orgId,
				workspaceId,
				name,
				userId,
				creatorEmail,
				pagination,
			});
			const res = await this.client.listAudiences(params, options);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async duplicateAudience(id: string): Promise<DuplicateAudienceResponse> {
		try {
			const params: DuplicateAudienceRequest = create(DuplicateAudienceRequestSchema, {
				id,
			});
			const res = await this.client.duplicateAudience(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteAudience(id: string): Promise<DeleteAudienceResponse> {
		try {
			const params: DeleteAudienceRequest = create(DeleteAudienceRequestSchema, {
				id,
			});
			const res = await this.client.deleteAudience(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createAudience(audience: Audience): Promise<CreateAudienceResponse> {
		try {
			const params: CreateAudienceRequest = create(CreateAudienceRequestSchema, { audience });
			const res = await this.client.createAudience(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async recalculateAudience(id: string): Promise<CalculateAudienceResponse> {
		try {
			const params: CalculateAudienceRequest = create(CalculateAudienceRequestSchema, {
				calulator: {
					case: "id",
					value: id,
				},
			});
			const res = await this.client.calculateAudience(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateAudience(audience: Audience): Promise<UpdateAudienceResponse> {
		try {
			const params: UpdateAudienceRequest = create(UpdateAudienceRequestSchema, { audience, mask: create(FieldMaskSchema, { paths: ["name", "description", "type", "config"] }) });
			const res = await this.client.updateAudience(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async calculateAudienceFilters(orgId: string, workspaceId: string, config: AudienceConfig, type: AudienceType): Promise<CalculateAudienceResponse> {
		try {
			const params: CalculateAudienceRequest = create(CalculateAudienceRequestSchema, {
				calulator: {
					case: "calculable",
					value: {
						orgId,
						workspaceId,
						config,
						type,
					},
				},
			});
			const res = await this.client.calculateAudience(params);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
